import React, { useEffect, useRef } from "react";
import { withV } from "../lib/media";

// Video que se reproduce solo de forma CONFIABLE en iOS/Safari y Android.
// Problemas que resuelve:
//  1) React no setea la PROPIEDAD `muted` del DOM (solo el atributo) => iOS
//     trata el video como "con sonido" y bloquea el autoplay. Lo forzamos.
//  2) iOS/Safari a veces bloquea el autoplay hasta la primera interacción o
//     con Modo de bajo consumo => arrancamos al primer toque/scroll global.
//  3) Reintenta reproducir cuando el video ya tiene datos y cuando entra en
//     pantalla (IntersectionObserver).
const AutoVideo = ({ src, className, style, poster, onLoadedMetadata }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Forzar propiedades necesarias para el autoplay inline en iOS.
    el.muted = true;
    el.defaultMuted = true;
    el.playsInline = true;
    el.setAttribute("muted", "");
    el.setAttribute("playsinline", "");
    el.setAttribute("webkit-playsinline", "true");

    let done = false;
    const tryPlay = () => {
      const p = el.play();
      if (p && p.catch)
        p.then(() => {
          done = true;
        }).catch(() => {});
    };

    // Intento inmediato + cuando haya datos.
    tryPlay();
    el.addEventListener("loadedmetadata", tryPlay);
    el.addEventListener("loadeddata", tryPlay);
    el.addEventListener("canplay", tryPlay);

    // Respaldo: primera interacción del usuario en cualquier parte.
    const onFirst = () => {
      tryPlay();
    };
    window.addEventListener("pointerdown", onFirst, { passive: true });
    window.addEventListener("touchstart", onFirst, { passive: true });
    window.addEventListener("scroll", onFirst, { passive: true });

    // Respaldo: reproducir cuando el video entra en pantalla.
    let io = null;
    if ("IntersectionObserver" in window) {
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting) tryPlay();
          });
        },
        { threshold: 0.1 }
      );
      io.observe(el);
    }

    // Forzar carga.
    try {
      el.load();
      tryPlay();
    } catch (e) {}

    return () => {
      el.removeEventListener("loadedmetadata", tryPlay);
      el.removeEventListener("loadeddata", tryPlay);
      el.removeEventListener("canplay", tryPlay);
      window.removeEventListener("pointerdown", onFirst);
      window.removeEventListener("touchstart", onFirst);
      window.removeEventListener("scroll", onFirst);
      if (io) io.disconnect();
    };
  }, [src]);

  return (
    <video
      ref={ref}
      src={withV(src)}
      onLoadedMetadata={onLoadedMetadata}
      className={className}
      style={style}
      poster={poster}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
    />
  );
};

export default AutoVideo;
